import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Package,
  ShoppingCart,
  Settings,
  Plus,
  Edit2,
  Trash2,
  CreditCard,
  Eye,
  BarChart3,
  AlertTriangle,
  Users,
  TrendingUp,
  Lock,
  LogOut,
  Mail,
  Gift,
  FileText,
  Image,
  Upload,
  Save,
  Palette,
  X,
  Calendar,
  Search,
  Download,
  Filter,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  FolderTree,
} from 'lucide-react';
import useStore from '../store/useStore';
import { translations } from '../i18n/translations';
import {
  getProducts,
  getOrders,
  deleteProduct,
  getPaymentSettings,
  updatePaymentSettings,
  updateOrderStatus,
  getAdminDashboard,
  getStockAlerts,
  adminLogin,
  setupAdmin,
  checkAdmin,
  deleteOrder,
  getOrderStatistics,
  getCategories,
} from '../api/api';

// Admin Login Component
const AdminLogin = ({ onLogin, language }) => {
  const [mode, setMode] = useState('login'); // 'login' or 'setup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('Admin');
  const [lastName, setLastName] = useState('EBB-SHOP');
  const [setupKey, setSetupKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await adminLogin({ email, password });
      localStorage.setItem('ebb_token', data.token);
      localStorage.setItem('ebb_user', JSON.stringify(data.user));
      localStorage.setItem('ebb_admin', 'true');
      onLogin(data.user);
    } catch (err) {
      setError(
        language === 'fr'
          ? 'Email ou mot de passe incorrect, ou compte non-admin'
          : 'Onjuist e-mailadres of wachtwoord, of geen admin-account'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const data = await setupAdmin({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        setup_key: setupKey,
      });
      localStorage.setItem('ebb_token', data.token);
      localStorage.setItem('ebb_user', JSON.stringify(data.user));
      localStorage.setItem('ebb_admin', 'true');
      onLogin(data.user);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          (language === 'fr' ? 'Erreur lors de la création' : 'Fout bij het aanmaken')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-amber-700" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {language === 'fr' ? 'Administration EBB-SHOP' : 'EBB-SHOP Administratie'}
          </h1>
          <p className="text-gray-500 mt-2">
            {language === 'fr' ? 'Connectez-vous pour accéder au panel admin' : 'Log in om toegang te krijgen tot het admin paneel'}
          </p>
        </div>

        {/* Toggle Mode */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setMode('login')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === 'login'
                ? 'bg-amber-700 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {language === 'fr' ? 'Connexion' : 'Inloggen'}
          </button>
          <button
            onClick={() => setMode('setup')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
              mode === 'setup'
                ? 'bg-amber-700 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {language === 'fr' ? 'Premier admin' : 'Eerste admin'}
          </button>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {mode === 'login' ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                required
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {language === 'fr' ? 'Mot de passe' : 'Wachtwoord'}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                required
                data-testid="admin-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-700 hover:bg-amber-800 disabled:bg-gray-400 text-white py-3 rounded-lg font-semibold transition-colors"
              data-testid="admin-login-btn"
            >
              {loading
                ? (language === 'fr' ? 'Connexion...' : 'Inloggen...')
                : (language === 'fr' ? 'Se connecter' : 'Inloggen')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSetup} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'fr' ? 'Prénom' : 'Voornaam'}
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'fr' ? 'Nom' : 'Achternaam'}
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {language === 'fr' ? 'Mot de passe' : 'Wachtwoord'}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                required
                minLength={6}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {language === 'fr' ? "Clé d'installation" : 'Installatiesleutel'}
              </label>
              <input
                type="password"
                value={setupKey}
                onChange={(e) => setSetupKey(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                placeholder="ebb-admin-setup-2024"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {language === 'fr'
                  ? 'Clé par défaut: ebb-admin-setup-2024'
                  : 'Standaard sleutel: ebb-admin-setup-2024'}
              </p>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-amber-700 hover:bg-amber-800 disabled:bg-gray-400 text-white py-3 rounded-lg font-semibold transition-colors"
            >
              {loading
                ? (language === 'fr' ? 'Création...' : 'Aanmaken...')
                : (language === 'fr' ? 'Créer le compte admin' : 'Admin-account aanmaken')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  const { language } = useStore();
  const t = translations[language];

  // Admin authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [paymentSettings, setPaymentSettings] = useState({});
  const [dashboard, setDashboard] = useState(null);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPayment, setEditingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({});
  const [selectedOrder, setSelectedOrder] = useState(null); // For order detail modal
  
  // Statistics state
  const [statistics, setStatistics] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsFilters, setStatsFilters] = useState({
    startDate: '',
    endDate: '',
    customerName: '',
  });
  const [expandedOrders, setExpandedOrders] = useState({});
  const [sortOrder, setSortOrder] = useState('none'); // 'none', 'asc', 'desc'
  
  // Personnalisation state
  const [customization, setCustomization] = useState({
    logo_url: '',
    logo_width: 40,
    logo_height: 40,
    shop_name: 'EBB-SHOP',
    hero_image: '',
    category_images: {
      sweaters: '',
      hoodies: '',
      tshirts: '',
      jackets: '',
    },
    about_hero_image: '',
    about_story_image: '',
    about_gallery_images: ['', '', '', ''],
    chatbot_enabled: true,
    // Shipping settings
    free_shipping_threshold: 75.0,
    shipping_cost: 5.95,
  });
  const [savingCustomization, setSavingCustomization] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null); // Track which field is uploading
  const [cloudinaryEnabled, setCloudinaryEnabled] = useState(false);
  const [dynamicCategories, setDynamicCategories] = useState([]);
  
  // Users management state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    is_admin: false
  });
  
  // Function to get category name from slug
  const getCategoryName = (slug) => {
    if (!slug) return '-';
    // First try dynamic categories from API
    const dynamicCat = dynamicCategories.find(c => c.slug === slug);
    if (dynamicCat) {
      return dynamicCat.translations?.[language]?.name || dynamicCat.translations?.fr?.name || slug;
    }
    // Fallback to translations
    if (t.nav[slug]) {
      return t.nav[slug];
    }
    // Last resort: return the slug itself (capitalized)
    return slug.charAt(0).toUpperCase() + slug.slice(1);
  };
  
  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await getCategories();
        if (data && Array.isArray(data)) {
          setDynamicCategories(data.filter(cat => !cat.legacy));
        }
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    if (isAuthenticated) {
      loadCategories();
    }
  }, [isAuthenticated, language]);
  
  // Check Cloudinary status on mount
  useEffect(() => {
    const checkCloudinary = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/cloudinary/status`);
        const data = await response.json();
        setCloudinaryEnabled(data.configured);
      } catch (e) {
        setCloudinaryEnabled(false);
      }
    };
    checkCloudinary();
  }, []);

  // Helper to extract Cloudinary public_id from URL
  const getCloudinaryPublicId = (url) => {
    if (!url || !url.includes('res.cloudinary.com')) return null;
    try {
      const uploadIndex = url.indexOf('/upload/');
      if (uploadIndex === -1) return null;
      let path = url.substring(uploadIndex + 8);
      if (path.match(/^v\d+\//)) {
        path = path.replace(/^v\d+\//, '');
      }
      const lastDot = path.lastIndexOf('.');
      if (lastDot !== -1) {
        path = path.substring(0, lastDot);
      }
      return path;
    } catch (e) {
      return null;
    }
  };

  // Delete image from Cloudinary
  const deleteCloudinaryImage = async (imageUrl) => {
    const publicId = getCloudinaryPublicId(imageUrl);
    if (publicId && cloudinaryEnabled) {
      try {
        const token = localStorage.getItem('ebb_token');
        await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/cloudinary/${encodeURIComponent(publicId)}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } catch (e) {
        console.warn('Could not delete old Cloudinary image:', e);
      }
    }
  };

  // Upload image function
  const handleImageUpload = async (file, fieldType, category = null) => {
    if (!file) return;
    
    const uploadKey = category ? `${fieldType}_${category}` : fieldType;
    setUploadingImage(uploadKey);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    
    try {
      const token = localStorage.getItem('ebb_token');
      let imageUrl;
      
      // Get old image URL to delete after successful upload
      let oldImageUrl = null;
      if (fieldType === 'logo') {
        oldImageUrl = customization.logo_url;
      } else if (fieldType === 'hero') {
        oldImageUrl = customization.hero_image;
      } else if (fieldType === 'about_hero') {
        oldImageUrl = customization.about_hero_image;
      } else if (fieldType === 'about_story') {
        oldImageUrl = customization.about_story_image;
      } else if (fieldType === 'about_gallery' && category !== null) {
        oldImageUrl = customization.about_gallery_images[category];
      } else if (fieldType === 'category' && category) {
        oldImageUrl = customization.category_images[category];
      }
      
      if (cloudinaryEnabled) {
        // Upload to Cloudinary - use appropriate folder
        const uploadFolder = fieldType === 'logo' ? 'logos/' : 'uploads/';
        const sigResponse = await fetch(`${BACKEND_URL}/api/cloudinary/signature?folder=${uploadFolder}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const sigData = await sigResponse.json();
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('api_key', sigData.api_key);
        formData.append('timestamp', sigData.timestamp);
        formData.append('signature', sigData.signature);
        formData.append('folder', sigData.folder);
        
        const uploadResponse = await fetch(
          `https://api.cloudinary.com/v1_1/${sigData.cloud_name}/image/upload`,
          { method: 'POST', body: formData }
        );
        
        const result = await uploadResponse.json();
        imageUrl = result.secure_url;
      } else {
        // Fallback to local upload
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${BACKEND_URL}/api/upload/image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
        
        if (!response.ok) throw new Error('Upload failed');
        
        const data = await response.json();
        imageUrl = `${BACKEND_URL}${data.url}`;
      }
      
      // Delete old image from Cloudinary if it was a Cloudinary image
      if (oldImageUrl && oldImageUrl.includes('res.cloudinary.com')) {
        await deleteCloudinaryImage(oldImageUrl);
      }
      
      // Update the appropriate field
      if (fieldType === 'logo') {
        setCustomization(prev => ({ ...prev, logo_url: imageUrl }));
      } else if (fieldType === 'hero') {
        setCustomization(prev => ({ ...prev, hero_image: imageUrl }));
      } else if (fieldType === 'about_hero') {
        setCustomization(prev => ({ ...prev, about_hero_image: imageUrl }));
      } else if (fieldType === 'about_story') {
        setCustomization(prev => ({ ...prev, about_story_image: imageUrl }));
      } else if (fieldType === 'about_gallery' && category !== null) {
        setCustomization(prev => {
          const newGallery = [...(prev.about_gallery_images || ['', '', '', ''])];
          newGallery[category] = imageUrl;
          return { ...prev, about_gallery_images: newGallery };
        });
      } else if (fieldType === 'category' && category) {
        setCustomization(prev => ({
          ...prev,
          category_images: {
            ...prev.category_images,
            [category]: imageUrl
          }
        }));
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(language === 'fr' ? 'Erreur lors de l\'upload' : 'Upload mislukt');
    } finally {
      setUploadingImage(null);
    }
  };

  // Check if user is already logged in as admin
  useEffect(() => {
    const checkAdminAuth = async () => {
      const token = localStorage.getItem('ebb_token');
      const isAdmin = localStorage.getItem('ebb_admin');
      
      if (token && isAdmin) {
        try {
          const result = await checkAdmin();
          if (result.is_admin) {
            const user = JSON.parse(localStorage.getItem('ebb_user') || '{}');
            setAdminUser(user);
            setIsAuthenticated(true);
          } else {
            // Not admin, clear admin flag
            localStorage.removeItem('ebb_admin');
          }
        } catch (err) {
          localStorage.removeItem('ebb_admin');
        }
      }
      setCheckingAuth(false);
    };

    checkAdminAuth();
  }, []);

  const handleAdminLogin = (user) => {
    setAdminUser(user);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('ebb_admin');
    setIsAuthenticated(false);
    setAdminUser(null);
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [activeTab, isAuthenticated]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        const data = await getAdminDashboard();
        setDashboard(data);
        const alerts = await getStockAlerts();
        setStockAlerts(alerts);
      } else if (activeTab === 'products') {
        const data = await getProducts({ limit: 100 });
        setProducts(data);
      } else if (activeTab === 'orders') {
        const data = await getOrders({ limit: 100 });
        setOrders(data);
      } else if (activeTab === 'settings') {
        const data = await getPaymentSettings();
        setPaymentSettings(data);
        setPaymentForm(data);
      } else if (activeTab === 'customization') {
        // Load site settings for customization
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const response = await fetch(`${BACKEND_URL}/api/settings/site`);
        if (response.ok) {
          const data = await response.json();
          // Note: API returns camelCase keys (logoUrl, shopName, etc.)
          setCustomization({
            logo_url: data.logoUrl || data.logo_url || '',
            logo_width: data.logoWidth || data.logo_width || 40,
            logo_height: data.logoHeight || data.logo_height || 40,
            shop_name: data.shopName || data.shop_name || 'EBB-SHOP',
            hero_image: data.heroImage || data.hero_image || '',
            category_images: data.categoryImages || data.category_images || {
              sweaters: '',
              hoodies: '',
              tshirts: '',
              jackets: '',
            },
            about_hero_image: data.aboutHeroImage || data.about_hero_image || '',
            about_story_image: data.aboutStoryImage || data.about_story_image || '',
            about_gallery_images: data.aboutGalleryImages || data.about_gallery_images || ['', '', '', ''],
            chatbot_enabled: data.chatbotEnabled !== false && data.chatbot_enabled !== false,
            // Shipping settings - handle both camelCase and snake_case
            free_shipping_threshold: data.freeShippingThreshold ?? data.free_shipping_threshold ?? 75.0,
            shipping_cost: data.shippingCost ?? data.shipping_cost ?? 5.95,
          });
        }
      } else if (activeTab === 'users') {
        // Load users
        await loadUsers();
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Users management functions
  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('ebb_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('ebb_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userForm)
      });
      
      if (response.ok) {
        setShowUserModal(false);
        setUserForm({ email: '', password: '', first_name: '', last_name: '', phone: '', is_admin: false });
        loadUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Erreur lors de la création');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('ebb_token');
      const updateData = { ...userForm };
      if (!updateData.password) {
        delete updateData.password;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        setShowUserModal(false);
        setEditingUser(null);
        setUserForm({ email: '', password: '', first_name: '', last_name: '', phone: '', is_admin: false });
        loadUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm(language === 'fr' ? 'Supprimer cet utilisateur ?' : 'Deze gebruiker verwijderen?')) {
      return;
    }
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const token = localStorage.getItem('ebb_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        loadUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      email: user.email,
      password: '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      is_admin: user.is_admin || false
    });
    setShowUserModal(true);
  };

  const openCreateUser = () => {
    setEditingUser(null);
    setUserForm({ email: '', password: '', first_name: '', last_name: '', phone: '', is_admin: false });
    setShowUserModal(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm(t.admin.confirmDelete)) return;
    try {
      await deleteProduct(productId);
      setProducts((prev) => prev.filter((p) => p.id !== productId));
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await updateOrderStatus(orderId, status);
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status } : o))
      );
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  // Delete order and restore stock
  const handleDeleteOrder = async (orderId, orderNumber, invoiceNumber) => {
    // Step 1: Confirm deletion
    const confirmDelete = language === 'fr' 
      ? `Êtes-vous sûr de vouloir supprimer la commande ${orderNumber} ?\n\nLe stock sera automatiquement restauré.`
      : `Weet u zeker dat u bestelling ${orderNumber} wilt verwijderen?\n\nDe voorraad wordt automatisch hersteld.`;
    
    if (!window.confirm(confirmDelete)) return;
    
    // Step 2: Ask about invoice deletion if invoice exists
    let deleteInvoice = true;
    if (invoiceNumber) {
      deleteInvoice = window.confirm(
        language === 'fr' 
          ? `Voulez-vous également supprimer la facture associée (${invoiceNumber}) ?`
          : `Wilt u ook de bijbehorende factuur (${invoiceNumber}) verwijderen?`
      );
    }
    
    try {
      const result = await deleteOrder(orderId, deleteInvoice);
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      
      // Show detailed result
      let message = language === 'fr' 
        ? `✅ Commande ${orderNumber} supprimée\n\n` 
        : `✅ Bestelling ${orderNumber} verwijderd\n\n`;
      
      if (result.stock_restored?.length > 0) {
        message += language === 'fr' ? '📦 Stock restauré:\n' : '📦 Voorraad hersteld:\n';
        message += result.stock_restored.join('\n');
        message += '\n\n';
      }
      
      if (result.invoice_deleted) {
        message += language === 'fr' 
          ? `🧾 Facture ${result.invoice_number} supprimée` 
          : `🧾 Factuur ${result.invoice_number} verwijderd`;
      }
      
      alert(message);
    } catch (error) {
      console.error('Error deleting order:', error);
      alert(language === 'fr' ? 'Erreur lors de la suppression' : 'Fout bij verwijderen');
    }
  };

  // Load statistics
  const handleLoadStatistics = async () => {
    setStatsLoading(true);
    try {
      const data = await getOrderStatistics(statsFilters);
      setStatistics(data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  // Export statistics to CSV
  const handleExportCSV = () => {
    if (!statistics?.detailed_report?.length) return;
    
    const headers = [
      'N° Commande', 'Date', 'Statut', 'Prénom', 'Nom', 'Email',
      'Article', 'Taille', 'Couleur', 'Quantité', 'Prix Unit.', 'Total Ligne'
    ];
    
    const rows = statistics.detailed_report.map(row => [
      row.order_number,
      new Date(row.order_date).toLocaleDateString('fr-BE'),
      row.status,
      row.customer_first_name,
      row.customer_last_name,
      row.customer_email,
      row.product_name,
      row.size,
      row.color,
      row.quantity,
      row.unit_price.toFixed(2),
      row.line_total.toFixed(2),
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(';'))
      .join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `statistiques_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleSavePaymentSettings = async () => {
    try {
      const updated = await updatePaymentSettings(paymentForm);
      setPaymentSettings(updated);
      setEditingPayment(false);
    } catch (error) {
      console.error('Error saving payment settings:', error);
    }
  };

  // Save customization settings
  const handleSaveCustomization = async () => {
    setSavingCustomization(true);
    try {
      const token = localStorage.getItem('ebb_token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      // Send data with snake_case keys to ensure consistency in DB
      const dataToSave = {
        logo_url: customization.logo_url,
        logo_width: customization.logo_width,
        logo_height: customization.logo_height,
        shop_name: customization.shop_name,
        hero_image: customization.hero_image,
        category_images: customization.category_images,
        about_hero_image: customization.about_hero_image,
        about_story_image: customization.about_story_image,
        about_gallery_images: customization.about_gallery_images,
        chatbot_enabled: customization.chatbot_enabled,
        free_shipping_threshold: customization.free_shipping_threshold,
        shipping_cost: customization.shipping_cost,
      };
      
      const response = await fetch(`${BACKEND_URL}/api/settings/site`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(dataToSave),
      });
      
      if (response.ok) {
        // Reload site settings in the global store without reloading the page
        try {
          const settingsResponse = await fetch(`${BACKEND_URL}/api/settings/site`);
          if (settingsResponse.ok) {
            const newSettings = await settingsResponse.json();
            // Update global store if available via window event
            window.dispatchEvent(new CustomEvent('site-settings-updated', { detail: newSettings }));
          }
        } catch (e) {
          console.warn('Could not refresh site settings:', e);
        }
        alert(language === 'fr' ? 'Personnalisation sauvegardée !' : 'Aanpassing opgeslagen!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving customization:', error);
      alert(language === 'fr' ? 'Erreur lors de la sauvegarde' : 'Fout bij opslaan');
    } finally {
      setSavingCustomization(false);
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    paid: 'bg-green-100 text-green-800',
    processing: 'bg-blue-100 text-blue-800',
    shipped: 'bg-purple-100 text-purple-800',
    delivered: 'bg-gray-100 text-gray-800',
    cancelled: 'bg-red-100 text-red-800',
  };

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-700 border-t-transparent"></div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleAdminLogin} language={language} />;
  }

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white print:min-h-0" data-testid="admin-page" id="admin-page-root">
      {/* Header - Hidden when printing */}
      <div className="bg-amber-900 py-8 print:hidden" id="admin-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-white">{t.admin.title}</h1>
            <p className="text-amber-200 text-sm mt-1">
              {language === 'fr' ? 'Connecté en tant que' : 'Ingelogd als'}: {adminUser?.email}
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            {language === 'fr' ? 'Déconnexion' : 'Uitloggen'}
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print:max-w-full print:p-0 print:m-0" id="admin-main-container">
        <div className="flex gap-8 print:block print:gap-0" id="admin-layout">
          {/* Sidebar - Hidden when printing */}
          <div className="w-64 flex-shrink-0 print:hidden" id="admin-sidebar">
            <nav className="bg-white rounded-lg shadow">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  activeTab === 'dashboard'
                    ? 'bg-amber-50 text-amber-700 border-l-4 border-amber-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <BarChart3 className="w-5 h-5" />
                {t.admin.dashboard}
              </button>
              <button
                onClick={() => setActiveTab('products')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  activeTab === 'products'
                    ? 'bg-amber-50 text-amber-700 border-l-4 border-amber-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Package className="w-5 h-5" />
                {t.admin.products}
              </button>
              <button
                onClick={() => setActiveTab('orders')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  activeTab === 'orders'
                    ? 'bg-amber-50 text-amber-700 border-l-4 border-amber-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ShoppingCart className="w-5 h-5" />
                {t.admin.orders}
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  activeTab === 'settings'
                    ? 'bg-amber-50 text-amber-700 border-l-4 border-amber-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <CreditCard className="w-5 h-5" />
                {t.admin.paymentSettings}
              </button>
              <button
                onClick={() => setActiveTab('customization')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  activeTab === 'customization'
                    ? 'bg-amber-50 text-amber-700 border-l-4 border-amber-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Palette className="w-5 h-5" />
                {language === 'fr' ? 'Personnalisation' : 'Aanpassing'}
              </button>
              <button
                onClick={() => setActiveTab('statistics')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  activeTab === 'statistics'
                    ? 'bg-amber-50 text-amber-700 border-l-4 border-amber-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <TrendingUp className="w-5 h-5" />
                {language === 'fr' ? 'Statistiques' : 'Statistieken'}
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  activeTab === 'users'
                    ? 'bg-amber-50 text-amber-700 border-l-4 border-amber-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Users className="w-5 h-5" />
                {language === 'fr' ? 'Utilisateurs' : 'Gebruikers'}
              </button>
              <button
                onClick={() => navigate('/admin/categories')}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors text-gray-700 hover:bg-gray-50 border-t border-gray-100"
                data-testid="admin-categories-btn"
              >
                <FolderTree className="w-5 h-5" />
                {language === 'fr' ? 'Catégories' : 'Categorieën'}
              </button>
              <button
                onClick={() => navigate('/admin/invoicing')}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors text-gray-700 hover:bg-gray-50 border-t border-gray-100"
              >
                <Mail className="w-5 h-5" />
                {language === 'fr' ? 'Facturation' : 'Facturatie'}
              </button>
              <button
                onClick={() => navigate('/admin/migration')}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors text-gray-700 hover:bg-gray-50 border-t border-gray-100"
              >
                <Settings className="w-5 h-5" />
                {language === 'fr' ? 'Config & Migration' : 'Config & Migratie'}
              </button>
              <button
                onClick={() => navigate('/admin/gift-cards')}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors text-gray-700 hover:bg-gray-50 border-t border-gray-100"
                data-testid="admin-gift-cards-btn"
              >
                <Gift className="w-5 h-5" />
                {language === 'fr' ? 'Cartes Cadeaux' : 'Cadeaubonnen'}
              </button>
              <button
                onClick={() => navigate('/admin/email-templates')}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors text-gray-700 hover:bg-gray-50 border-t border-gray-100"
                data-testid="admin-email-templates-btn"
              >
                <FileText className="w-5 h-5" />
                {language === 'fr' ? 'Templates Email' : 'E-mail Templates'}
              </button>
              <button
                onClick={() => navigate('/admin/documents')}
                className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors text-gray-700 hover:bg-gray-50 border-t border-gray-100"
                data-testid="admin-documents-btn"
              >
                <Download className="w-5 h-5" />
                {language === 'fr' ? 'Documents / PDF' : 'Documenten / PDF'}
              </button>
            </nav>
          </div>

          {/* Main Content - Full width when printing */}
          <div className="flex-1 print:w-full print:max-w-full" id="admin-content">
            {loading ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-700 border-t-transparent mx-auto"></div>
              </div>
            ) : (
              <>
                {/* Dashboard Tab */}
                {activeTab === 'dashboard' && dashboard && (
                  <div className="space-y-6">
                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">{t.admin.products}</p>
                            <p className="text-3xl font-bold text-gray-900">
                              {dashboard.products?.total || 0}
                            </p>
                          </div>
                          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                            <Package className="w-6 h-6 text-amber-700" />
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">{t.admin.orders}</p>
                            <p className="text-3xl font-bold text-gray-900">
                              {dashboard.orders?.total || 0}
                            </p>
                          </div>
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <ShoppingCart className="w-6 h-6 text-blue-700" />
                          </div>
                        </div>
                        <p className="text-sm text-yellow-600 mt-2">
                          {dashboard.orders?.pending || 0} {language === 'fr' ? 'en attente' : 'wachtend'}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">
                              {language === 'fr' ? 'Chiffre d\'affaires' : 'Omzet'}
                            </p>
                            <p className="text-3xl font-bold text-gray-900">
                              €{(dashboard.revenue?.total || 0).toFixed(2)}
                            </p>
                          </div>
                          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                            <TrendingUp className="w-6 h-6 text-green-700" />
                          </div>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg shadow p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">
                              {language === 'fr' ? 'Utilisateurs' : 'Gebruikers'}
                            </p>
                            <p className="text-3xl font-bold text-gray-900">
                              {dashboard.users?.total || 0}
                            </p>
                          </div>
                          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                            <Users className="w-6 h-6 text-purple-700" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Stock Alerts */}
                    {stockAlerts.length > 0 && (
                      <div className="bg-white rounded-lg shadow">
                        <div className="p-6 border-b flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-orange-500" />
                          <h2 className="text-xl font-bold text-gray-900">
                            {t.admin.stockAlerts} ({stockAlerts.length})
                          </h2>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                                  {language === 'fr' ? 'Produit' : 'Product'}
                                </th>
                                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                                  {language === 'fr' ? 'Variante' : 'Variant'}
                                </th>
                                <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900">
                                  Stock
                                </th>
                                <th className="px-6 py-3 text-center text-sm font-semibold text-gray-900">
                                  {t.admin.status}
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {stockAlerts.map((alert, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-6 py-4">
                                    <Link
                                      to={`/admin/product/${alert.product_id}`}
                                      className="text-amber-700 hover:text-amber-800 font-medium"
                                    >
                                      {alert.product_name}
                                    </Link>
                                  </td>
                                  <td className="px-6 py-4 text-gray-600">
                                    {alert.variant}
                                  </td>
                                  <td className="px-6 py-4 text-center">
                                    <span className="font-bold text-red-600">
                                      {alert.current_stock}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-center">
                                    <span
                                      className={`px-2 py-1 rounded text-xs font-medium ${
                                        alert.status === 'out_of_stock'
                                          ? 'bg-red-100 text-red-700'
                                          : 'bg-orange-100 text-orange-700'
                                      }`}
                                    >
                                      {alert.status === 'out_of_stock'
                                        ? t.admin.outOfStock
                                        : t.admin.lowStock}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Products Tab */}
                {activeTab === 'products' && (
                  <div className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b flex justify-between items-center">
                      <h2 className="text-xl font-bold text-gray-900">
                        {t.admin.products} ({products.length})
                      </h2>
                      <button
                        onClick={() => navigate('/admin/product/new')}
                        className="flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-4 py-2 rounded-lg transition-colors"
                      >
                        <Plus className="w-5 h-5" />
                        {t.admin.addProduct}
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                              {language === 'fr' ? 'Produit' : 'Product'}
                            </th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                              {language === 'fr' ? 'Catégorie' : 'Categorie'}
                            </th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                              {t.products.price}
                            </th>
                            <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                              {language === 'fr' ? 'Stock' : 'Voorraad'}
                            </th>
                            <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {products.map((product) => {
                            const trans = product.translations?.[language] || product.translations?.fr || {};
                            const totalStock = product.variants?.reduce((sum, v) => sum + v.stock, 0) || 0;
                            return (
                              <tr key={product.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-3">
                                    <img
                                      src={product.images?.[0] || 'https://via.placeholder.com/50'}
                                      alt=""
                                      className="w-12 h-12 object-cover rounded"
                                    />
                                    <div>
                                      <p className="font-medium text-gray-900">{trans.name}</p>
                                      {product.featured && (
                                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                          Featured
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-gray-600">
                                  {getCategoryName(product.category)}
                                </td>
                                <td className="px-6 py-4">
                                  <span className="font-medium text-amber-900">
                                    €{product.price?.toFixed(2)}
                                  </span>
                                  {product.original_price && (
                                    <span className="ml-2 text-sm text-gray-400 line-through">
                                      €{product.original_price?.toFixed(2)}
                                    </span>
                                  )}
                                </td>
                                <td className="px-6 py-4">
                                  <span
                                    className={`px-2 py-1 rounded text-sm ${
                                      totalStock > 10
                                        ? 'bg-green-100 text-green-700'
                                        : totalStock > 0
                                        ? 'bg-yellow-100 text-yellow-700'
                                        : 'bg-red-100 text-red-700'
                                    }`}
                                  >
                                    {totalStock}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex justify-end gap-2">
                                    <Link
                                      to={`/product/${product.id}`}
                                      className="p-2 text-gray-500 hover:text-amber-700"
                                      title={t.products.viewDetails}
                                    >
                                      <Eye className="w-5 h-5" />
                                    </Link>
                                    <button
                                      onClick={() => navigate(`/admin/product/${product.id}`)}
                                      className="p-2 text-gray-500 hover:text-amber-700"
                                      title={t.admin.editProduct}
                                    >
                                      <Edit2 className="w-5 h-5" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteProduct(product.id)}
                                      className="p-2 text-gray-500 hover:text-red-600"
                                      title={t.admin.deleteProduct}
                                    >
                                      <Trash2 className="w-5 h-5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Orders Tab */}
                {activeTab === 'orders' && (
                  <div className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b">
                      <h2 className="text-xl font-bold text-gray-900">
                        {t.admin.orders} ({orders.length})
                      </h2>
                    </div>
                    {orders.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        {language === 'fr' ? 'Aucune commande' : 'Geen bestellingen'}
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                                {t.checkout.orderNumber}
                              </th>
                              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                                {language === 'fr' ? 'Client' : 'Klant'}
                              </th>
                              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                                {t.cart.total}
                              </th>
                              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                                {t.admin.status}
                              </th>
                              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900">
                                Date
                              </th>
                              <th className="px-6 py-3 text-right text-sm font-semibold text-gray-900">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {orders.map((order) => (
                              <tr key={order.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                  <button
                                    onClick={() => setSelectedOrder(order)}
                                    className="font-medium text-amber-700 hover:text-amber-800 hover:underline"
                                  >
                                    {order.order_number}
                                  </button>
                                </td>
                                <td className="px-6 py-4">
                                  <p className="text-gray-900">
                                    {order.customer_info?.first_name} {order.customer_info?.last_name}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    {order.customer_info?.email}
                                  </p>
                                </td>
                                <td className="px-6 py-4 font-medium">
                                  €{order.total?.toFixed(2)}
                                </td>
                                <td className="px-6 py-4">
                                  <span
                                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                                      statusColors[order.status] || statusColors.pending
                                    }`}
                                  >
                                    {t.admin.orderStatus[order.status] || order.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-gray-500">
                                  {new Date(order.created_at).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4">
                                  <div className="flex items-center gap-2">
                                    <select
                                      value={order.status}
                                      onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                                      className="border rounded px-2 py-1 text-sm focus:ring-2 focus:ring-amber-500"
                                    >
                                      <option value="pending">{t.admin.orderStatus.pending}</option>
                                      <option value="paid">{t.admin.orderStatus.paid}</option>
                                      <option value="processing">{t.admin.orderStatus.processing}</option>
                                      <option value="shipped">{t.admin.orderStatus.shipped}</option>
                                      <option value="delivered">{t.admin.orderStatus.delivered}</option>
                                      <option value="cancelled">{t.admin.orderStatus.cancelled}</option>
                                    </select>
                                    <button
                                      onClick={() => handleDeleteOrder(order.id, order.order_number, order.invoice_number)}
                                      className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                      title={language === 'fr' ? 'Supprimer la commande' : 'Bestelling verwijderen'}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Statistics Tab */}
                {activeTab === 'statistics' && (
                  <div className="space-y-6">
                    {/* Filters - Hidden when printing */}
                    <div className="bg-white rounded-lg shadow p-6 print:hidden">
                      <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Filter className="w-5 h-5" />
                        {language === 'fr' ? 'Filtres' : 'Filters'}
                      </h2>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Date début' : 'Startdatum'}
                          </label>
                          <input
                            type="date"
                            value={statsFilters.startDate}
                            onChange={(e) => setStatsFilters(prev => ({ ...prev, startDate: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Date fin' : 'Einddatum'}
                          </label>
                          <input
                            type="date"
                            value={statsFilters.endDate}
                            onChange={(e) => setStatsFilters(prev => ({ ...prev, endDate: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Nom du client' : 'Klantnaam'}
                          </label>
                          <input
                            type="text"
                            value={statsFilters.customerName}
                            onChange={(e) => setStatsFilters(prev => ({ ...prev, customerName: e.target.value }))}
                            placeholder={language === 'fr' ? 'Rechercher...' : 'Zoeken...'}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                        <div className="flex items-center gap-1 mt-6"> {/* Changed to items-center for perfect button alignment */}
                          <button
                            onClick={handleLoadStatistics}
                            disabled={statsLoading}
                            className="flex-1 flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-4 py-2 rounded-lg transition-colors disabled:bg-gray-400"
                          >
                            <Search className="w-4 h-4" />
                            {statsLoading ? '...' : (language === 'fr' ? 'Rechercher' : 'Zoeken')}
                          </button>
                          <button
                            onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                            className="flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-800 text-white px-4 py-2 rounded-lg transition-colors"
                            title={language === 'fr' ? 'Trier par nom de famille' : 'Sorteren op achternaam'}
                          >
                            <ArrowUpDown className="w-4 h-4" />
                            <span className="hidden md:inline whitespace-nowrap">{sortOrder === 'asc' ? 'A-Z' : 'Z-A'}</span>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Summary Cards - Hidden when printing */}
                    {statistics && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:hidden">
                          <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                <ShoppingCart className="w-6 h-6 text-blue-600" />
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">{language === 'fr' ? 'Total commandes' : 'Totaal bestellingen'}</p>
                                <p className="text-2xl font-bold text-gray-900">{statistics.summary.total_orders}</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                <TrendingUp className="w-6 h-6 text-green-600" />
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">{language === 'fr' ? 'Chiffre d\'affaires' : 'Omzet'}</p>
                                <p className="text-2xl font-bold text-gray-900">€{statistics.summary.total_revenue.toFixed(2)}</p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                                <CreditCard className="w-6 h-6 text-amber-600" />
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">{language === 'fr' ? 'Panier moyen' : 'Gem. bestelling'}</p>
                                <p className="text-2xl font-bold text-gray-900">€{statistics.summary.average_order_value.toFixed(2)}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Professional Printable Report */}
                        <div className="bg-white rounded-lg shadow" id="statistics-report">
                          {/* Header with actions */}
                          <div className="p-4 border-b flex justify-between items-center print:hidden">
                            <h2 className="text-xl font-bold text-gray-900">
                              {language === 'fr' ? 'Liste des commandes' : 'Bestellingenlijst'}
                            </h2>
                            <div className="flex gap-2">
                              <button
                                onClick={() => window.print()}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                              >
                                <FileText className="w-4 h-4" />
                                {language === 'fr' ? 'Imprimer' : 'Afdrukken'}
                              </button>
                              <button
                                onClick={handleExportCSV}
                                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                              >
                                <Download className="w-4 h-4" />
                                CSV
                              </button>
                            </div>
                          </div>
                          
                          {/* Print Header - Ultra Professional - only visible when printing */}
                          <div className="hidden print:block print-header-professional">
                            {/* Top Bar with gradient */}
                            <div className="print-header-topbar"></div>
                            
                            {/* Main Header Content */}
                            <div className="print-header-content">
                              {/* Left: Logo & Brand */}
                              <div className="print-header-brand">
                                <div className="print-logo-container">
                                  {customization.logo_url ? (
                                    <img 
                                      src={customization.logo_url} 
                                      alt="Logo" 
                                      className="print-logo-image"
                                    />
                                  ) : (
                                    <div className="print-logo-icon">
                                      <svg viewBox="0 0 24 24" fill="currentColor" className="print-horse-icon">
                                        <path d="M21.5 9.5c-.28 0-.55.04-.81.11L18.5 6.5C18.5 5.67 17.83 5 17 5h-2l-2-2h-2v2H9.5C8.67 5 8 5.67 8 6.5v2H6L4 12H2v2h2l2 4h1.5l.5-2h8l.5 2H18l2-4h2v-2h-2l-2.5-2.5zm-7.5.5a1 1 0 100-2 1 1 0 000 2z"/>
                                      </svg>
                                    </div>
                                  )}
                                  <div className="print-brand-text">
                                    <span className="print-brand-name">EBB-SHOP</span>
                                    <span className="print-brand-tagline">{language === 'fr' ? 'Vêtements Équestres Premium' : 'Premium Ruitersportkleding'}</span>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Right: Document Info */}
                              <div className="print-header-info">
                                <div className="print-doc-type">
                                  {language === 'fr' ? 'RAPPORT DES COMMANDES' : 'BESTELLINGENRAPPORT'}
                                </div>
                                <div className="print-doc-period">
                                  {statsFilters.startDate && statsFilters.endDate 
                                    ? `${new Date(statsFilters.startDate).toLocaleDateString('fr-BE')} — ${new Date(statsFilters.endDate).toLocaleDateString('fr-BE')}`
                                    : (language === 'fr' ? 'Toutes les périodes' : 'Alle perioden')}
                                </div>
                              </div>
                            </div>
                            
                            {/* Bottom Bar with date */}
                            <div className="print-header-bottom">
                              <div className="print-header-line"></div>
                              <div className="print-header-date">
                                <span>{language === 'fr' ? 'Document généré le' : 'Document gegenereerd op'}</span>
                                <strong>{new Date().toLocaleDateString('fr-BE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</strong>
                                <span>{language === 'fr' ? 'à' : 'om'} {new Date().toLocaleTimeString('fr-BE', {hour: '2-digit', minute: '2-digit'})}</span>
                              </div>
                            </div>
                          </div>

                          {statistics.orders.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                              {language === 'fr' ? 'Aucune commande trouvée' : 'Geen bestellingen gevonden'}
                            </div>
                          ) : (
                            <div className="p-4">
                              {(() => {
                                const sortedOrders = [...statistics.orders].sort((a, b) => {
                                  if (sortOrder === 'none') return 0;
                                  const nameA = (a.customer_info?.last_name || '').toLowerCase();
                                  const nameB = (b.customer_info?.last_name || '').toLowerCase();
                                  if (sortOrder === 'asc') return nameA.localeCompare(nameB);
                                  if (sortOrder === 'desc') return nameB.localeCompare(nameA);
                                  return 0;
                                });

                                return sortedOrders.map((order, orderIndex) => (
                                  <div 
                                    key={order.id} 
                                    className="customer-block mb-6 border border-gray-300 rounded-lg overflow-hidden"
                                    style={{ pageBreakInside: 'avoid' }}
                                  >
                                    {/* Customer Name Header */}
                                    <div className="customer-header bg-gradient-to-r from-amber-100 to-amber-50 px-4 py-3 border-b-2 border-amber-300">
                                      <div className="flex justify-between items-center">
                                        <div className="customer-name text-lg font-bold text-gray-900">
                                          {order.customer_info?.last_name?.toUpperCase() || ''} {order.customer_info?.first_name || ''}
                                        </div>
                                        <div className="text-right">
                                          <span className="font-mono text-sm font-semibold text-amber-800">{order.order_number}</span>
                                          <span className="mx-2 text-gray-400">|</span>
                                          <span className="text-sm text-gray-600">{new Date(order.created_at).toLocaleDateString('fr-BE')}</span>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Items List */}
                                    <table className="items-table w-full text-sm">
                                      <thead>
                                        <tr className="bg-gray-100">
                                          <th className="px-4 py-2 text-left font-semibold text-gray-700 w-2/5">
                                            {language === 'fr' ? 'Article' : 'Artikel'}
                                          </th>
                                          <th className="px-4 py-2 text-center font-semibold text-gray-700 w-1/6">
                                            {language === 'fr' ? 'Taille' : 'Maat'}
                                          </th>
                                          <th className="px-4 py-2 text-center font-semibold text-gray-700 w-1/6">
                                            {language === 'fr' ? 'Couleur' : 'Kleur'}
                                          </th>
                                          <th className="px-4 py-2 text-center font-semibold text-gray-700 w-1/12">
                                            {language === 'fr' ? 'Qté' : 'Aantal'}
                                          </th>
                                          <th className="px-4 py-2 text-right font-semibold text-gray-700 w-1/6">
                                            {language === 'fr' ? 'Prix' : 'Prijs'}
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {order.items?.map((item, idx) => (
                                          <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                            <td className="px-4 py-2 font-medium text-gray-900">
                                              {item.name}
                                            </td>
                                            <td className="px-4 py-2 text-center text-gray-700">
                                              <span className="inline-block bg-gray-200 px-2 py-0.5 rounded text-xs font-medium">
                                                {item.size || '-'}
                                              </span>
                                            </td>
                                            <td className="px-4 py-2 text-center text-gray-700">
                                              {item.color || '-'}
                                            </td>
                                            <td className="px-4 py-2 text-center font-bold text-gray-900">
                                              {item.quantity}
                                            </td>
                                            <td className="px-4 py-2 text-right font-medium text-gray-900">
                                              €{(item.price * item.quantity).toFixed(2)}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                      <tfoot>
                                        <tr className="total-row bg-amber-50 border-t-2 border-amber-300">
                                          <td colSpan="4" className="px-4 py-3 text-right font-bold text-gray-800">
                                            {language === 'fr' ? 'TOTAL PAYÉ :' : 'TOTAAL BETAALD:'}
                                          </td>
                                          <td className="px-4 py-3 text-right font-bold text-amber-700 text-lg">
                                            €{order.total?.toFixed(2)}
                                          </td>
                                        </tr>
                                      </tfoot>
                                    </table>
                                  </div>
                                ));
                              })()}

                              {/* Print Summary Footer - Ultra Professional */}
                              <div className="hidden print:block print-footer-professional">
                                {/* Top decorative line */}
                                <div className="print-footer-line"></div>
                                
                                {/* Summary Title */}
                                <div className="print-footer-title">
                                  <span>{language === 'fr' ? 'RÉCAPITULATIF' : 'SAMENVATTING'}</span>
                                </div>
                                
                                {/* Stats Cards */}
                                <div className="print-footer-stats">
                                  <div className="print-stat-card">
                                    <div className="print-stat-icon print-stat-icon-orders">
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                                      </svg>
                                    </div>
                                    <div className="print-stat-content">
                                      <p className="print-stat-label">
                                        {language === 'fr' ? 'Commandes' : 'Bestellingen'}
                                      </p>
                                      <p className="print-stat-value">{statistics.summary.total_orders}</p>
                                    </div>
                                  </div>
                                  
                                  <div className="print-stat-card print-stat-card-highlight">
                                    <div className="print-stat-icon print-stat-icon-revenue">
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
                                      </svg>
                                    </div>
                                    <div className="print-stat-content">
                                      <p className="print-stat-label">
                                        {language === 'fr' ? 'Chiffre d\'affaires' : 'Totale omzet'}
                                      </p>
                                      <p className="print-stat-value print-stat-value-green">€{statistics.summary.total_revenue.toFixed(2)}</p>
                                    </div>
                                  </div>
                                  
                                  <div className="print-stat-card">
                                    <div className="print-stat-icon print-stat-icon-average">
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M3 3v18h18M18 17V9M13 17V5M8 17v-3"/>
                                      </svg>
                                    </div>
                                    <div className="print-stat-content">
                                      <p className="print-stat-label">
                                        {language === 'fr' ? 'Panier moyen' : 'Gem. bestelling'}
                                      </p>
                                      <p className="print-stat-value print-stat-value-amber">€{statistics.summary.average_order_value.toFixed(2)}</p>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Footer bottom bar */}
                                <div className="print-footer-bottom">
                                  <div className="print-footer-brand">
                                    <span className="print-footer-logo">EBB-SHOP</span>
                                    <span className="print-footer-separator">•</span>
                                    <span className="print-footer-tagline">{language === 'fr' ? 'Vêtements Équestres Premium' : 'Premium Ruitersportkleding'}</span>
                                  </div>
                                  <div className="print-footer-gradient"></div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                    
                    {!statistics && (
                      <div className="bg-white rounded-lg shadow p-12 text-center">
                        <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500">
                          {language === 'fr' 
                            ? 'Utilisez les filtres ci-dessus et cliquez sur "Rechercher" pour afficher les statistiques'
                            : 'Gebruik de filters hierboven en klik op "Zoeken" om statistieken te bekijken'}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Payment Settings Tab */}
                {activeTab === 'settings' && (
                  <div className="bg-white rounded-lg shadow">
                    <div className="p-6 border-b flex justify-between items-center">
                      <h2 className="text-xl font-bold text-gray-900">
                        {t.admin.paymentSettings}
                      </h2>
                      {!editingPayment && (
                        <button
                          onClick={() => setEditingPayment(true)}
                          className="flex items-center gap-2 text-amber-700 hover:text-amber-800"
                        >
                          <Edit2 className="w-5 h-5" />
                          {language === 'fr' ? 'Modifier' : 'Bewerken'}
                        </button>
                      )}
                    </div>
                    <div className="p-6">
                      <p className="text-gray-600 mb-6">
                        {language === 'fr'
                          ? 'Configurez vos données bancaires pour le paiement par QR code. Ces informations seront affichées aux clients lors du checkout.'
                          : 'Configureer uw bankgegevens voor QR-code betalingen. Deze informatie wordt getoond aan klanten bij het afrekenen.'}
                      </p>

                      {editingPayment ? (
                        <div className="space-y-4 max-w-md">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t.admin.bankName}
                            </label>
                            <input
                              type="text"
                              value={paymentForm.bank_name || ''}
                              onChange={(e) =>
                                setPaymentForm({ ...paymentForm, bank_name: e.target.value })
                              }
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                              placeholder="ex: ING, BNP Paribas Fortis..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t.checkout.accountHolder}
                            </label>
                            <input
                              type="text"
                              value={paymentForm.account_holder || ''}
                              onChange={(e) =>
                                setPaymentForm({ ...paymentForm, account_holder: e.target.value })
                              }
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                              placeholder="ex: EBB-SHOP SPRL"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t.checkout.iban}
                            </label>
                            <input
                              type="text"
                              value={paymentForm.iban || ''}
                              onChange={(e) =>
                                setPaymentForm({ ...paymentForm, iban: e.target.value })
                              }
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 font-mono"
                              placeholder="BE00 0000 0000 0000"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t.checkout.bic}
                            </label>
                            <input
                              type="text"
                              value={paymentForm.bic || ''}
                              onChange={(e) =>
                                setPaymentForm({ ...paymentForm, bic: e.target.value })
                              }
                              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 font-mono"
                              placeholder="BBRUBEBB"
                            />
                          </div>
                          <div className="flex gap-4 pt-4">
                            <button
                              onClick={handleSavePaymentSettings}
                              className="flex items-center gap-2 bg-amber-700 hover:bg-amber-800 text-white px-6 py-2 rounded-lg transition-colors"
                            >
                              {t.admin.save}
                            </button>
                            <button
                              onClick={() => {
                                setEditingPayment(false);
                                setPaymentForm(paymentSettings);
                              }}
                              className="flex items-center gap-2 border border-gray-300 px-6 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                              {t.admin.cancel}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-gray-50 rounded-lg p-6 max-w-md">
                          <dl className="space-y-4">
                            <div>
                              <dt className="text-sm text-gray-500">{t.admin.bankName}</dt>
                              <dd className="font-medium text-gray-900">
                                {paymentSettings.bank_name || (
                                  <span className="text-gray-400 italic">
                                    {language === 'fr' ? 'Non configuré' : 'Niet geconfigureerd'}
                                  </span>
                                )}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm text-gray-500">{t.checkout.accountHolder}</dt>
                              <dd className="font-medium text-gray-900">
                                {paymentSettings.account_holder || (
                                  <span className="text-gray-400 italic">
                                    {language === 'fr' ? 'Non configuré' : 'Niet geconfigureerd'}
                                  </span>
                                )}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm text-gray-500">{t.checkout.iban}</dt>
                              <dd className="font-medium text-gray-900 font-mono">
                                {paymentSettings.iban || (
                                  <span className="text-gray-400 italic font-sans">
                                    {language === 'fr' ? 'Non configuré' : 'Niet geconfigureerd'}
                                  </span>
                                )}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-sm text-gray-500">{t.checkout.bic}</dt>
                              <dd className="font-medium text-gray-900 font-mono">
                                {paymentSettings.bic || (
                                  <span className="text-gray-400 italic font-sans">
                                    {language === 'fr' ? 'Non configuré' : 'Niet geconfigureerd'}
                                  </span>
                                )}
                              </dd>
                            </div>
                          </dl>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Customization Tab */}
                {activeTab === 'customization' && (
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <Palette className="w-6 h-6 text-amber-600" />
                        {language === 'fr' ? 'Personnalisation du site' : 'Website aanpassing'}
                      </h2>
                      <button
                        onClick={handleSaveCustomization}
                        disabled={savingCustomization}
                        className="flex items-center gap-2 bg-amber-700 text-white px-4 py-2 rounded-lg hover:bg-amber-800 transition-colors disabled:opacity-50"
                      >
                        <Save className="w-4 h-4" />
                        {savingCustomization 
                          ? (language === 'fr' ? 'Sauvegarde...' : 'Opslaan...') 
                          : (language === 'fr' ? 'Sauvegarder' : 'Opslaan')}
                      </button>
                    </div>

                    <div className="space-y-8">
                      {/* Logo Section */}
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Image className="w-5 h-5 text-amber-600" />
                          {language === 'fr' ? 'Logo du site' : 'Website logo'}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium mb-2">
                              {language === 'fr' ? 'Nom de la boutique' : 'Winkelnaam'}
                            </label>
                            <input
                              type="text"
                              value={customization.shop_name}
                              onChange={(e) => setCustomization(prev => ({ ...prev, shop_name: e.target.value }))}
                              className="w-full px-4 py-2 border rounded-lg"
                              placeholder="EBB-SHOP"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">
                              {language === 'fr' ? 'Logo (Upload ou URL)' : 'Logo (Upload of URL)'}
                            </label>
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={customization.logo_url}
                                onChange={(e) => setCustomization(prev => ({ ...prev, logo_url: e.target.value }))}
                                className="flex-1 px-4 py-2 border rounded-lg"
                                placeholder="https://... ou uploadez"
                              />
                              <label className="cursor-pointer bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 flex items-center gap-2">
                                {uploadingImage === 'logo' ? (
                                  <span className="animate-spin">⏳</span>
                                ) : (
                                  <Upload className="w-4 h-4" />
                                )}
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => handleImageUpload(e.target.files[0], 'logo')}
                                  disabled={uploadingImage === 'logo'}
                                />
                              </label>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">
                              {language === 'fr' ? 'Largeur du logo (px)' : 'Logo breedte (px)'}
                            </label>
                            <input
                              type="number"
                              value={customization.logo_width}
                              onChange={(e) => setCustomization(prev => ({ ...prev, logo_width: parseInt(e.target.value) || 40 }))}
                              className="w-full px-4 py-2 border rounded-lg"
                              min="20"
                              max="200"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">
                              {language === 'fr' ? 'Hauteur du logo (px)' : 'Logo hoogte (px)'}
                            </label>
                            <input
                              type="number"
                              value={customization.logo_height}
                              onChange={(e) => setCustomization(prev => ({ ...prev, logo_height: parseInt(e.target.value) || 40 }))}
                              className="w-full px-4 py-2 border rounded-lg"
                              min="20"
                              max="200"
                            />
                          </div>
                          {customization.logo_url && (
                            <div className="col-span-2">
                              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                                {language === 'fr' ? 'Image actuelle' : 'Huidige afbeelding'}
                                <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">
                                  ✓ {language === 'fr' ? 'En cours d\'utilisation' : 'In gebruik'}
                                </span>
                              </label>
                              <div className="bg-white p-4 rounded-lg border inline-block relative group">
                                <img 
                                  src={customization.logo_url} 
                                  alt="Logo preview"
                                  style={{ 
                                    width: customization.logo_width, 
                                    height: customization.logo_height,
                                    objectFit: 'contain'
                                  }}
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (window.confirm(language === 'fr' ? 'Supprimer ce logo ?' : 'Deze logo verwijderen?')) {
                                      await deleteCloudinaryImage(customization.logo_url);
                                      setCustomization(prev => ({ ...prev, logo_url: '' }));
                                    }
                                  }}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                  title={language === 'fr' ? 'Supprimer' : 'Verwijderen'}
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Hero Image Section */}
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Image className="w-5 h-5 text-amber-600" />
                          {language === 'fr' ? 'Image d\'accueil (Hero)' : 'Hero afbeelding'}
                        </h3>
                        <div>
                          <label className="block text-sm font-medium mb-2">
                            {language === 'fr' ? 'Image (Upload ou URL)' : 'Afbeelding (Upload of URL)'}
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={customization.hero_image}
                              onChange={(e) => setCustomization(prev => ({ ...prev, hero_image: e.target.value }))}
                              className="flex-1 px-4 py-2 border rounded-lg"
                              placeholder="https://... ou uploadez"
                            />
                            <label className="cursor-pointer bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 flex items-center gap-2">
                              {uploadingImage === 'hero' ? (
                                <span className="animate-spin">⏳</span>
                              ) : (
                                <Upload className="w-4 h-4" />
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(e.target.files[0], 'hero')}
                                disabled={uploadingImage === 'hero'}
                              />
                            </label>
                          </div>
                          {customization.hero_image && (
                            <div className="mt-4">
                              <label className="block text-sm font-medium mb-2 flex items-center gap-2">
                                {language === 'fr' ? 'Image actuelle' : 'Huidige afbeelding'}
                                <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">
                                  ✓ {language === 'fr' ? 'En cours d\'utilisation' : 'In gebruik'}
                                </span>
                              </label>
                              <div className="relative inline-block group">
                                <img 
                                  src={customization.hero_image} 
                                  alt="Hero preview"
                                  className="max-w-md rounded-lg shadow"
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (window.confirm(language === 'fr' ? 'Supprimer cette image ?' : 'Deze afbeelding verwijderen?')) {
                                      await deleteCloudinaryImage(customization.hero_image);
                                      setCustomization(prev => ({ ...prev, hero_image: '' }));
                                    }
                                  }}
                                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                  title={language === 'fr' ? 'Supprimer' : 'Verwijderen'}
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* About Page Images Section */}
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Image className="w-5 h-5 text-amber-600" />
                          {language === 'fr' ? 'Images de la page À propos' : 'Over ons pagina afbeeldingen'}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                          {language === 'fr' 
                            ? 'Gérez les images affichées sur la page "À propos" : 1 image Hero, 1 image Story et 4 images galerie.'
                            : 'Beheer de afbeeldingen op de "Over ons" pagina: 1 Hero afbeelding, 1 Story afbeelding en 4 galerij afbeeldingen.'}
                        </p>
                        
                        {/* About Hero Image */}
                        <div className="bg-white rounded-lg p-4 border mb-4">
                          <label className="block text-sm font-medium mb-2">
                            {language === 'fr' ? '🖼️ Image Hero (bandeau principal)' : '🖼️ Hero afbeelding (hoofdbanner)'}
                          </label>
                          <div className="flex gap-2 mb-2">
                            <input
                              type="text"
                              value={customization.about_hero_image || ''}
                              onChange={(e) => setCustomization(prev => ({ ...prev, about_hero_image: e.target.value }))}
                              className="flex-1 px-3 py-2 border rounded-lg text-sm"
                              placeholder="URL ou uploadez"
                            />
                            <label className="cursor-pointer bg-amber-600 text-white px-3 py-2 rounded-lg hover:bg-amber-700 flex items-center">
                              {uploadingImage === 'about_hero' ? (
                                <span className="animate-spin text-sm">⏳</span>
                              ) : (
                                <Upload className="w-4 h-4" />
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(e.target.files[0], 'about_hero')}
                                disabled={uploadingImage === 'about_hero'}
                              />
                            </label>
                          </div>
                          {customization.about_hero_image && (
                            <div className="relative group inline-block">
                              <img 
                                src={customization.about_hero_image} 
                                alt="About Hero"
                                className="w-full max-w-md h-32 object-cover rounded"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  if (window.confirm(language === 'fr' ? 'Supprimer cette image ?' : 'Deze afbeelding verwijderen?')) {
                                    deleteCloudinaryImage(customization.about_hero_image);
                                    setCustomization(prev => ({ ...prev, about_hero_image: '' }));
                                  }
                                }}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* About Story Image */}
                        <div className="bg-white rounded-lg p-4 border mb-4">
                          <label className="block text-sm font-medium mb-2">
                            {language === 'fr' ? '📖 Image Story (Notre Passion)' : '📖 Story afbeelding (Onze Passie)'}
                          </label>
                          <div className="flex gap-2 mb-2">
                            <input
                              type="text"
                              value={customization.about_story_image || ''}
                              onChange={(e) => setCustomization(prev => ({ ...prev, about_story_image: e.target.value }))}
                              className="flex-1 px-3 py-2 border rounded-lg text-sm"
                              placeholder="URL ou uploadez"
                            />
                            <label className="cursor-pointer bg-amber-600 text-white px-3 py-2 rounded-lg hover:bg-amber-700 flex items-center">
                              {uploadingImage === 'about_story' ? (
                                <span className="animate-spin text-sm">⏳</span>
                              ) : (
                                <Upload className="w-4 h-4" />
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => handleImageUpload(e.target.files[0], 'about_story')}
                                disabled={uploadingImage === 'about_story'}
                              />
                            </label>
                          </div>
                          {customization.about_story_image && (
                            <div className="relative group inline-block">
                              <img 
                                src={customization.about_story_image} 
                                alt="About Story"
                                className="w-48 h-32 object-cover rounded"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  if (window.confirm(language === 'fr' ? 'Supprimer cette image ?' : 'Deze afbeelding verwijderen?')) {
                                    deleteCloudinaryImage(customization.about_story_image);
                                    setCustomization(prev => ({ ...prev, about_story_image: '' }));
                                  }
                                }}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* About Gallery Images (4) */}
                        <div className="bg-white rounded-lg p-4 border">
                          <label className="block text-sm font-medium mb-3">
                            {language === 'fr' ? '🎨 Galerie "Notre Univers" (4 images)' : '🎨 Galerij "Onze Wereld" (4 afbeeldingen)'}
                          </label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[0, 1, 2, 3].map((index) => (
                              <div key={index} className="bg-gray-50 rounded-lg p-3 border">
                                <label className="block text-xs font-medium mb-1 text-gray-500">
                                  {language === 'fr' ? `Image ${index + 1}` : `Afbeelding ${index + 1}`}
                                </label>
                                <div className="flex gap-2 mb-2">
                                  <input
                                    type="text"
                                    value={customization.about_gallery_images?.[index] || ''}
                                    onChange={(e) => {
                                      setCustomization(prev => {
                                        const newGallery = [...(prev.about_gallery_images || ['', '', '', ''])];
                                        newGallery[index] = e.target.value;
                                        return { ...prev, about_gallery_images: newGallery };
                                      });
                                    }}
                                    className="flex-1 px-2 py-1.5 border rounded text-sm"
                                    placeholder="URL ou uploadez"
                                  />
                                  <label className="cursor-pointer bg-amber-600 text-white px-2 py-1.5 rounded hover:bg-amber-700 flex items-center">
                                    {uploadingImage === `about_gallery_${index}` ? (
                                      <span className="animate-spin text-sm">⏳</span>
                                    ) : (
                                      <Upload className="w-4 h-4" />
                                    )}
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={(e) => handleImageUpload(e.target.files[0], 'about_gallery', index)}
                                      disabled={uploadingImage === `about_gallery_${index}`}
                                    />
                                  </label>
                                </div>
                                {customization.about_gallery_images?.[index] && (
                                  <div className="relative group inline-block">
                                    <img 
                                      src={customization.about_gallery_images[index]} 
                                      alt={`Gallery ${index + 1}`}
                                      className="w-full h-24 object-cover rounded"
                                      onError={(e) => e.target.style.display = 'none'}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (window.confirm(language === 'fr' ? 'Supprimer cette image ?' : 'Deze afbeelding verwijderen?')) {
                                          deleteCloudinaryImage(customization.about_gallery_images[index]);
                                          setCustomization(prev => {
                                            const newGallery = [...(prev.about_gallery_images || ['', '', '', ''])];
                                            newGallery[index] = '';
                                            return { ...prev, about_gallery_images: newGallery };
                                          });
                                        }
                                      }}
                                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Chatbot Toggle */}
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4">
                          {language === 'fr' ? 'Chatbot' : 'Chatbot'}
                        </h3>
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={customization.chatbot_enabled}
                            onChange={(e) => setCustomization(prev => ({ ...prev, chatbot_enabled: e.target.checked }))}
                            className="w-5 h-5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                          />
                          <span className="text-sm">
                            {language === 'fr' 
                              ? 'Activer le chatbot sur le site' 
                              : 'Chatbot activeren op de website'}
                          </span>
                        </label>
                      </div>

                      {/* Shipping Settings */}
                      <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
                        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                          <Package className="w-5 h-5 text-blue-600" />
                          {language === 'fr' ? 'Frais de livraison' : 'Verzendkosten'}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium mb-2">
                              {language === 'fr' ? 'Frais de livraison (€)' : 'Verzendkosten (€)'}
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={customization.shipping_cost}
                              onChange={(e) => setCustomization(prev => ({ ...prev, shipping_cost: parseFloat(e.target.value) || 0 }))}
                              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              {language === 'fr' 
                                ? 'Montant facturé si le panier n\'atteint pas le franco' 
                                : 'Bedrag gefactureerd als de winkelwagen het minimumbedrag niet bereikt'}
                            </p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">
                              {language === 'fr' ? 'Franco / Livraison gratuite dès (€)' : 'Gratis verzending vanaf (€)'}
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={customization.free_shipping_threshold}
                              onChange={(e) => setCustomization(prev => ({ ...prev, free_shipping_threshold: parseFloat(e.target.value) || 0 }))}
                              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              {language === 'fr' 
                                ? 'Livraison offerte si le panier atteint ce montant (0 = jamais gratuit)' 
                                : 'Gratis verzending als het winkelwagentje dit bedrag bereikt (0 = nooit gratis)'}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <strong>{language === 'fr' ? 'Résumé' : 'Samenvatting'}:</strong>{' '}
                            {customization.free_shipping_threshold > 0 ? (
                              language === 'fr'
                                ? `Livraison à €${customization.shipping_cost.toFixed(2)} si le panier est inférieur à €${customization.free_shipping_threshold.toFixed(2)}, sinon GRATUIT.`
                                : `Verzending €${customization.shipping_cost.toFixed(2)} als winkelwagen onder €${customization.free_shipping_threshold.toFixed(2)}, anders GRATIS.`
                            ) : (
                              language === 'fr'
                                ? `Livraison toujours à €${customization.shipping_cost.toFixed(2)}`
                                : `Verzending altijd €${customization.shipping_cost.toFixed(2)}`
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex justify-between items-center mb-6">
                      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <Users className="w-6 h-6 text-amber-600" />
                        {language === 'fr' ? 'Gestion des utilisateurs' : 'Gebruikersbeheer'}
                      </h2>
                      <button
                        onClick={openCreateUser}
                        className="flex items-center gap-2 bg-amber-700 text-white px-4 py-2 rounded-lg hover:bg-amber-800 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                        {language === 'fr' ? 'Nouvel utilisateur' : 'Nieuwe gebruiker'}
                      </button>
                    </div>

                    {usersLoading ? (
                      <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-700 mx-auto"></div>
                        <p className="mt-4 text-gray-500">
                          {language === 'fr' ? 'Chargement...' : 'Laden...'}
                        </p>
                      </div>
                    ) : users.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">
                          {language === 'fr' ? 'Aucun utilisateur trouvé' : 'Geen gebruikers gevonden'}
                        </p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                                {language === 'fr' ? 'Nom' : 'Naam'}
                              </th>
                              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                                Email
                              </th>
                              <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">
                                {language === 'fr' ? 'Rôle' : 'Rol'}
                              </th>
                              <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">
                                {language === 'fr' ? 'Créé le' : 'Aangemaakt'}
                              </th>
                              <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {users.map((user) => (
                              <tr key={user.id} className="hover:bg-gray-50">
                                <td className="px-4 py-4">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                                      <span className="text-amber-700 font-medium">
                                        {(user.first_name?.[0] || user.email[0]).toUpperCase()}
                                      </span>
                                    </div>
                                    <div>
                                      <p className="font-medium text-gray-900">
                                        {user.first_name} {user.last_name}
                                      </p>
                                      {user.phone && (
                                        <p className="text-sm text-gray-500">{user.phone}</p>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-4 text-gray-600">{user.email}</td>
                                <td className="px-4 py-4 text-center">
                                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                    user.is_admin 
                                      ? 'bg-purple-100 text-purple-700' 
                                      : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {user.is_admin 
                                      ? (language === 'fr' ? 'Admin' : 'Admin') 
                                      : (language === 'fr' ? 'Client' : 'Klant')}
                                  </span>
                                </td>
                                <td className="px-4 py-4 text-center text-sm text-gray-500">
                                  {user.created_at 
                                    ? new Date(user.created_at).toLocaleDateString() 
                                    : '-'}
                                </td>
                                <td className="px-4 py-4">
                                  <div className="flex justify-end gap-2">
                                    <button
                                      onClick={() => openEditUser(user)}
                                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                      title={language === 'fr' ? 'Modifier' : 'Bewerken'}
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    {!user.is_admin && (
                                      <button
                                        onClick={() => handleDeleteUser(user.id)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title={language === 'fr' ? 'Supprimer' : 'Verwijderen'}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Order Detail Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b flex justify-between items-center bg-amber-50">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {language === 'fr' ? 'Détails de la commande' : 'Bestelgegevens'}
                </h3>
                <p className="text-amber-700 font-mono font-bold">{selectedOrder.order_number}</p>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              {/* Order Status */}
              <div className="mb-6">
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${statusColors[selectedOrder.status] || statusColors.pending}`}>
                  {t.admin.orderStatus[selectedOrder.status] || selectedOrder.status}
                </span>
                <span className="ml-4 text-gray-500">
                  {new Date(selectedOrder.created_at).toLocaleString()}
                </span>
              </div>

              {/* Customer Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {language === 'fr' ? 'Client' : 'Klant'}
                  </h4>
                  <p className="font-medium">{selectedOrder.customer_info?.first_name} {selectedOrder.customer_info?.last_name}</p>
                  <p className="text-gray-600">{selectedOrder.customer_info?.email}</p>
                  <p className="text-gray-600">{selectedOrder.customer_info?.phone}</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    {language === 'fr' ? 'Adresse de livraison' : 'Leveringsadres'}
                  </h4>
                  <p className="font-medium">{selectedOrder.shipping_address?.address}</p>
                  <p className="text-gray-600">{selectedOrder.shipping_address?.postal_code} {selectedOrder.shipping_address?.city}</p>
                  <p className="text-gray-600">{selectedOrder.shipping_address?.country}</p>
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">
                  {language === 'fr' ? 'Articles commandés' : 'Bestelde artikelen'}
                </h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                          {language === 'fr' ? 'Article' : 'Artikel'}
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">
                          {language === 'fr' ? 'Taille' : 'Maat'}
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">
                          {language === 'fr' ? 'Couleur' : 'Kleur'}
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">
                          {language === 'fr' ? 'Qté' : 'Aantal'}
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                          {language === 'fr' ? 'Prix' : 'Prijs'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {selectedOrder.items?.map((item, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {item.image && (
                                <img src={item.image} alt="" className="w-12 h-12 object-cover rounded" />
                              )}
                              <span className="font-medium">{item.name || item.product_id}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="bg-gray-100 px-2 py-1 rounded text-sm font-medium">
                              {item.size || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="bg-gray-100 px-2 py-1 rounded text-sm">
                              {item.color || '-'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center font-medium">{item.quantity}</td>
                          <td className="px-4 py-3 text-right font-medium">€{(item.price * item.quantity).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Invoice Reference */}
              {selectedOrder.invoice_number && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-700">
                    <span className="font-medium">{language === 'fr' ? 'Facture associée :' : 'Gekoppelde factuur:'}</span>{' '}
                    <span className="font-mono font-bold">{selectedOrder.invoice_number}</span>
                  </p>
                </div>
              )}

              {/* Total */}
              <div className="bg-amber-50 rounded-lg p-4 flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">
                  {language === 'fr' ? 'Total' : 'Totaal'}
                </span>
                <span className="text-2xl font-bold text-amber-700">
                  €{selectedOrder.total?.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setSelectedOrder(null)}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                {language === 'fr' ? 'Fermer' : 'Sluiten'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Create/Edit Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">
                {editingUser 
                  ? (language === 'fr' ? 'Modifier l\'utilisateur' : 'Gebruiker bewerken')
                  : (language === 'fr' ? 'Nouvel utilisateur' : 'Nieuwe gebruiker')}
              </h3>
              <button
                onClick={() => {
                  setShowUserModal(false);
                  setEditingUser(null);
                  setUserForm({ email: '', password: '', first_name: '', last_name: '', phone: '', is_admin: false });
                }}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'fr' ? 'Prénom' : 'Voornaam'}
                  </label>
                  <input
                    type="text"
                    value={userForm.first_name}
                    onChange={(e) => setUserForm({ ...userForm, first_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'fr' ? 'Nom' : 'Achternaam'}
                  </label>
                  <input
                    type="text"
                    value={userForm.last_name}
                    onChange={(e) => setUserForm({ ...userForm, last_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  required
                  disabled={!!editingUser}
                />
                {editingUser && (
                  <p className="text-xs text-gray-500 mt-1">
                    {language === 'fr' ? 'L\'email ne peut pas être modifié' : 'E-mail kan niet worden gewijzigd'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'fr' ? 'Mot de passe' : 'Wachtwoord'}
                  {editingUser && (
                    <span className="text-gray-400 font-normal ml-2">
                      ({language === 'fr' ? 'laisser vide pour ne pas changer' : 'leeg laten om niet te wijzigen'})
                    </span>
                  )}
                </label>
                <input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  required={!editingUser}
                  minLength={8}
                  placeholder={editingUser ? '••••••••' : ''}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'fr' ? 'Téléphone' : 'Telefoon'}
                </label>
                <input
                  type="tel"
                  value={userForm.phone}
                  onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  placeholder="+32 xxx xx xx xx"
                />
              </div>

              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="is_admin"
                  checked={userForm.is_admin}
                  onChange={(e) => setUserForm({ ...userForm, is_admin: e.target.checked })}
                  className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                />
                <label htmlFor="is_admin" className="text-sm font-medium text-gray-700">
                  {language === 'fr' ? 'Administrateur' : 'Beheerder'}
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowUserModal(false);
                    setEditingUser(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  {language === 'fr' ? 'Annuler' : 'Annuleren'}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-amber-700 text-white rounded-lg hover:bg-amber-800"
                >
                  {editingUser 
                    ? (language === 'fr' ? 'Mettre à jour' : 'Bijwerken')
                    : (language === 'fr' ? 'Créer' : 'Aanmaken')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;